import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import forms from './forms';
import data, * as fromData from './data';
import events, * as fromEvent from './event';
import auth, * as fromAuth from './auth';
import ui, * as fromUI from './ui';
import dashboard, * as fromDashboard from './dashboard';
import globalSearch, * as fromGlobalSearch from './global-search';
import alerts, * as fromAlerts from './alerts';
import calendar, * as fromCalendar from './calendar';
import reporting, * as fromReporting from './reporting';
import assignedLocation, * as fromAssignedLocation from './assigned-location';
import admin, * as fromAdmin from './admin';
import hasRequiredPermissions from '../helpers/has-required-permissions';

export default combineReducers({
    data,
    ui,
    auth,
    events,
    dashboard,
    globalSearch,
    alerts,
    calendar,
    reporting,
    assignedLocation,
    admin,
    ...forms
});

export const bindSelectorState = key => func => state => func(state[key]);
export const bindFromUI = bindSelectorState('ui');
export const bindFromData = bindSelectorState('data');
export const bindFromAuth = bindSelectorState('auth');
export const bindFromEvent = bindSelectorState('events');
export const bindFromDashboard = bindSelectorState('dashboard');
export const bindFromGlobalSearch = bindSelectorState('globalSearch');
export const bindFromAlerts = bindSelectorState('alerts');
export const bindFromCalendar = bindSelectorState('calendar');
export const bindFromReporting = bindSelectorState('reporting');
export const bindFromAssignedLocation = bindSelectorState('assignedLocation');
export const bindFromAdmin = bindSelectorState('admin');

//Admin
export const getCurrentlyAssignedTo = bindFromAdmin(
    fromAdmin.getCurrentlyAssignedTo
);
export const getAssignedData = bindFromAdmin(fromAdmin.getAssignedData);
export const getAssignedDataLoading = bindFromAdmin(
    fromAdmin.getAssignedDataLoading
);
export const getCounter = bindFromAdmin(fromAdmin.getCounter);
export const getReassignTo = bindFromAdmin(fromAdmin.getReassignTo);
export const getReassignSubmitLoading = bindFromAdmin(
    fromAdmin.getReassignSubmitLoading
);
export const getSuccessSubmission = bindFromAdmin(
    fromAdmin.getSuccessSubmission
);

//Assigned Location
export const getAssignmentOptions = bindFromAssignedLocation(
    fromAssignedLocation.getAssignmentOptions
);
export const getAssigneeOptions = bindFromAssignedLocation(
    fromAssignedLocation.getAssigneeOptions
);
export const getAssigneeLoading = bindFromAssignedLocation(
    fromAssignedLocation.getAssigneeLoading
);
export const getShowHideAssignedLocation = bindFromAssignedLocation(
    fromAssignedLocation.getShowHideAssignedLocation
);
export const getAssigneeUsername = bindFromAssignedLocation(
    fromAssignedLocation.getAssigneeUsername
);
export const getAssigneeId = bindFromAssignedLocation(
    fromAssignedLocation.getAssigneeId
);

//Calendar
export const getShowCalendar = bindFromCalendar(fromCalendar.getShowCalendar);
export const getShowCalendarToggle = bindFromCalendar(
    fromCalendar.getShowCalendarToggle
);
export const getActiveEvents = bindFromCalendar(fromCalendar.getActiveEvents);
export const getCalLoading = bindFromCalendar(fromCalendar.getCalLoading);
export const getCalendarRegionCodeList = bindFromCalendar(
    fromCalendar.getCalendarRegionCodeList
);
export const getCalendarDistrictCodeList = bindFromCalendar(
    fromCalendar.getCalendarDistrictCodeList
);
export const getCalendarRecruitingStationCodeList = bindFromCalendar(
    fromCalendar.getCalendarRecruitingStationCodeList
);
export const getCalendarRssOssCodeList = bindFromCalendar(
    fromCalendar.getCalendarRssOssCodeList
);

//Search
export const getSearchResults = bindFromGlobalSearch(
    fromGlobalSearch.getSearchResults
);
export const getSearchActive = bindFromGlobalSearch(
    fromGlobalSearch.getSearchActive
);
export const getSearchQuery = state => state.search.name;
export const getSearchResultsNumber = bindFromGlobalSearch(
    fromGlobalSearch.getSearchResultsNumber
);
export const getSearchLoading = bindFromGlobalSearch(
    fromGlobalSearch.getSearchLoading
);
export const getIsSearchFilterOpen = createSelector(
    [getSearchQuery, getSearchActive],
    (searchQuery, searchActive) => searchQuery.length && searchActive
);

export const getRegionCodeList = bindFromData(fromData.getRegionCodeList);
export const getDistrictCodeList = bindFromData(fromData.getDistrictCodeList);
export const getRecruitingStationCodeList = bindFromData(
    fromData.getRecruitingStationCodeList
);
export const getRssOssCodeList = bindFromData(fromData.getRssOssCodeList);
export const getSessionModal = bindFromUI(fromUI.getSessionModal);

//Reporting
export const getReportingRegionCodeList = bindFromReporting(
    fromReporting.getReportingRegionCodeList
);
export const getReportingDistrictCodeList = bindFromReporting(
    fromReporting.getReportingDistrictCodeList
);
export const getReportingRecruitingStationCodeList = bindFromReporting(
    fromReporting.getReportingRecruitingStationCodeList
);
export const getReportingRssOssCodeList = bindFromReporting(
    fromReporting.getReportingRssOssCodeList
);
export const getReportingData = bindFromReporting(
    fromReporting.getReportingData
);
export const getReportingCategoryList = bindFromReporting(
    fromReporting.getReportingCategoryList
);
export const getFilterBtnAction = bindFromReporting(
    fromReporting.getFilterBtnAction
);

export const getReportingLoading = bindFromReporting(
    fromReporting.getReportingLoading
);

//Dashboard
export const getDashboardETResultsNumber = bindFromDashboard(
    fromDashboard.getDashboardETResultsNumber
);
export const getDashboardCostsNumber = bindFromDashboard(
    fromDashboard.getDashboardCostsNumber
);
export const getEventStatusBreakDown = bindFromDashboard(
    fromDashboard.getEventStatusBreakDown
);
export const getTasksDueTotal = bindFromDashboard(
    fromDashboard.getTasksDueTotal
);
export const getTasksDueLoading = bindFromDashboard(
    fromDashboard.getTasksDueLoading
);

//Alerts
export const getAlertsLoading = bindFromAlerts(fromAlerts.getAlertsLoading);
export const getBadgeCount = bindFromAlerts(fromAlerts.getBadgeCount);
export const getTasksTouched = bindFromAlerts(fromAlerts.getTasksTouched);
export const getPastDueNeedsReviewEvents = bindFromAlerts(
    fromAlerts.getPastDueNeedsReviewEvents
);
export const getPastDueNeedsReviewAfterActions = bindFromAlerts(
    fromAlerts.getPastDueNeedsReviewAfterActions
);
export const getPastDueReturnedForEditEvents = bindFromAlerts(
    fromAlerts.getPastDueReturnedForEditEvents
);
export const getPastDueReturnedForEditAfterActions = bindFromAlerts(
    fromAlerts.getPastDueReturnedForEditAfterActions
);
export const getPastDueIncompleteAfterActions = bindFromAlerts(
    fromAlerts.getPastDueIncompleteAfterActions
);

export const getIncompleteEvents = bindFromAlerts(
    fromAlerts.getIncompleteEvents
);
export const getIncompleteAfterActions = bindFromAlerts(
    fromAlerts.getIncompleteAfterActions
);
export const getIncompleteAssignedAfterActions = bindFromAlerts(
    fromAlerts.getIncompleteAssignedAfterActions
);
export const getNeedsReviewEvents = bindFromAlerts(
    fromAlerts.getNeedsReviewEvents
);
export const getNeedsReviewAfterActions = bindFromAlerts(
    fromAlerts.getNeedsReviewAfterActions
);
export const getPendingApprovalEvents = bindFromAlerts(
    fromAlerts.getPendingApprovalEvents
);
export const getPendingApprovalAfterActions = bindFromAlerts(
    fromAlerts.getPendingApprovalAfterActions
);
export const getReturnedForEditEvents = bindFromAlerts(
    fromAlerts.getReturnedForEditEvents
);
export const getReturnedForEditAfterActions = bindFromAlerts(
    fromAlerts.getReturnedForEditAfterActions
);
export const getRejectedEvents = bindFromAlerts(fromAlerts.getRejectedEvents);

//User Information
export const getUserInfo = bindFromData(fromData.getUserInfo);
export const getTrustedUser = bindFromData(fromData.getTrustedUser);
export const getUserInfoLoading = bindFromData(fromData.getUserInfoLoading);
export const getUserPermissions = bindFromData(fromData.getUserPermissions);
export const getUserDisplayPermissions = bindFromData(
    fromData.getUserDisplayPermissions
);

//Authentication
export const getAuthentication = bindFromAuth(fromAuth.getAuthentication);
export const getJwtToken = bindFromAuth(fromAuth.getJwtToken);
export const getDevLoginErrorMessage = bindFromAuth(
    fromAuth.getDevLoginErrorMessage
);

//Event
export const getFundedRegionCodeList = bindFromData(
    fromData.getFundedRegionCodeList
);
export const getFundedDistrictCodeList = bindFromData(
    fromData.getFundedDistrictCodeList
);
export const getFundedRecruitingStationCodeList = bindFromData(
    fromData.getFundedRecruitingStationCodeList
);
export const getFundedRecruitingSubStationCodeList = bindFromData(
    fromData.getFundedRecruitingSubStationCodeList
);
export const getEventStatus = bindFromEvent(fromEvent.getEventStatus);
export const getEventLoading = bindFromEvent(fromEvent.getEventLoading);
export const getEventActionLoading = bindFromEvent(
    fromEvent.getEventActionLoading
);
export const getEventActionText = bindFromEvent(fromEvent.getEventActionText);
export const getPrLoading = bindFromEvent(fromEvent.getPrLoading);
export const getEventForm = state => state.forms.event;

export const eventActions = [
    {
        title: 'Save',
        classNames: 'btn btn--secondary',
        action: 'save',
        permissions: {
            Event: ['Create']
        },
        needsApprovers: true,
        needsCurrentId: false
    },
    {
        title: 'Submit',
        classNames: 'btn btn--submit',
        action: 'submit',
        permissions: {
            Event: ['Create']
        },
        needsApprovers: true,
        needsCurrentId: false
    },
    {
        title: 'Reject',
        classNames: 'btn btn--secondary',
        action: 'reject',
        permissions: {
            Event: ['Reject']
        },
        needsApprovers: false,
        needsCurrentId: true
    },
    {
        title: 'Approve',
        classNames: 'btn btn--secondary',
        action: 'approve',
        permissions: {
            Event: ['Review']
        },
        needsApprovers: false,
        needsCurrentId: true
    },
    {
        title: 'Request Info',
        classNames: 'btn btn--secondary',
        action: 'request',
        permissions: {
            Event: ['Review']
        },
        needsApprovers: false,
        needsCurrentId: true
    }
];
export const getEventActions = createSelector(
    [getUserPermissions, getEventStatus],
    (permissions, status) =>
        eventActions.filter(item =>
            hasRequiredPermissions(item.permissions, permissions)
        )
);

export const getCategoryList = bindFromData(fromData.getCategoryList);
export const getFiscalYearList = bindFromData(fromData.getFiscalYearList);

//Navigation
export const navItems = [
    {
        icon: 'add-event',
        title: 'Add Event',
        url: '/event',
        hasPermissions: permissions => {
            return hasRequiredPermissions(
                {
                    menu: ['create']
                },
                permissions
            );
        }
    },
    {
        icon: 'notification',
        title: 'Alerts / Task Queue',
        url: '/alerts',
        hasPermissions: permissions => {
            return hasRequiredPermissions(
                {
                    menu: ['alerts']
                },
                permissions
            );
        }
    },
    {
        icon: 'pie',
        title: 'Dashboard',
        url: '/dashboard',
        hasPermissions: permissions => {
            return hasRequiredPermissions(
                {
                    menu: ['dashboard']
                },
                permissions
            );
        }
    },
    {
        icon: 'calendar',
        title: 'Calendar',
        url: '/calendar',
        hasPermissions: permissions => {
            return hasRequiredPermissions(
                {
                    menu: ['calendar']
                },
                permissions
            );
        }
    },
    {
        icon: 'bar-graph',
        title: 'Reporting',
        url: '/reporting',
        hasPermissions: permissions => {
            return hasRequiredPermissions(
                {
                    menu: ['reporting']
                },
                permissions
            );
        }
    },
    {
        icon: 'admin',
        title: 'Admin',
        url: '/admin',
        hasPermissions: permissions => {
            return hasRequiredPermissions(
                {
                    menu: ['admin']
                },
                permissions
            );
        }
    }
];

export const getNavItems = createSelector(
    [getUserDisplayPermissions],
    permissions =>
        navItems.filter(
            item =>
                item.hasPermissions ? item.hasPermissions(permissions) : true
        )
);



// WEBPACK FOOTER //
// ./src/reducers/index.js